import { theme } from '@endure/ui';
/* https://mui.com/customization/theming/ */
import { type ThemeOptions, createTheme } from '@mui/material/styles';

const fontFamily = theme.fontFamily.sans.join(',');

export const FONTS = {
  primary: fontFamily,
  secondary: fontFamily,
};

export type ThemeMode = 'light' | 'dark';

export const getPalette = (mode: ThemeMode) => {
  const isLight = mode === 'light';

  return {
    mode,
    background: {
      default: isLight ? theme.colors['forge-light-bg'] : theme.colors.background,
      paper: isLight ? theme.colors['forge-light-elevated'] : theme.colors.cards,
    },
    secondary: {
      light: isLight ? theme.colors.lightGrey : theme.colors.grey,
      main: isLight ? theme.colors['forge-light-surface-2'] : theme.colors['dark-blue'],
    },
    text: {
      primary: isLight ? theme.colors['forge-light-fg'] : theme.colors.white,
      secondary: isLight ? theme.colors['forge-light-muted'] : theme.colors.grey,
    },
    button: {
      main: isLight ? theme.colors['forge-light-accent'] : theme.colors.blue,
      medium: isLight ? theme.colors['forge-light-accent-hover'] : theme.colors['blue-hover'],
      dark: isLight ? theme.colors['forge-light-accent-strong'] : theme.colors['blue-active'],
    },
    interactive: {
      primary: isLight ? theme.colors['forge-light-accent'] : theme.colors.blue,
      success: theme.colors.green,
      error: theme.colors.red,
      error50: 'rgba(233, 61, 68, 0.5)',
      warning: theme.colors.orange,
      hover: isLight ? theme.colors['forge-light-surface-2'] : theme.colors['background-hover'],
    },
  };
};

// Kept for existing static consumers such as Storybook stories. Runtime
// components receive the palette selected by themeStore through createMainTheme.
export const PALETTE = getPalette('light');

export const BREAKPOINTS = {
  values: {
    xs: 375,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1440,
  },
};

export const SPACING = 4;

export type TypographyVariant =
  | 'button'
  | 'caption'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'inherit'
  | 'overline'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'small1'
  | 'small2'
  | undefined;

export const SHAPE = {
  borderRadius: {
    verySmall: SPACING,
    small: SPACING * 2,
    medium: SPACING * 4,
    large: SPACING * 6,
  } as any, // our custom types seem to clash with the default MUI types
  iconSize: {
    small: SPACING * 3,
    medium: SPACING * 4,
    large: SPACING * 5,
    xLarge: SPACING * 6,
    xxLarge: SPACING * 10,
  },
  footerHeight: '56px',
  drawerWidthDesktop: '224px',
  drawerWidthTablet: '80px',
};

export const createMainTheme = (mode: ThemeMode) => {
  const palette = getPalette(mode);

  return createTheme({
    spacing: SPACING,
    palette,
    breakpoints: BREAKPOINTS,
    shape: SHAPE,
    typography: {
      fontFamily: FONTS.primary,
      color: palette.text.primary,
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 700,
        lineHeight: 1.5,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '0.3px',
        lineHeight: 1.5,
      },
      small1: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: palette.text.secondary,
        lineHeight: 1.5,
      },
      small2: {
        fontSize: '0.875rem',
        fontWeight: 400,
        color: palette.text.secondary,
        lineHeight: 1.5,
      },
      tiny: {
        fontSize: '0.75rem',
        lineHeight: 1.5,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: palette.background.paper,
            borderRadius: SPACING * 3,
            padding: SPACING * 6,
            boxShadow: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
          paper: {
            padding: 0,
            borderRadius: 0,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            top: 0,
            border: 'none',
          },
        },
      },
    },
  } as ThemeOptions);
};

export default createMainTheme('light');
